import { Route, Routes } from '@angular/router';
import { AnfrageGuard } from '../guards/anfrage/anfrage.guard';
import { AnfrageDetailComponent } from '../features/anfrage/forms/anfrage-detail/anfrage-detail.component';
import { GetAnfrageByIdResolver } from '../resolver/anfrage/get-anfrage-by-id.resolver';
import { GetAnfrageValidationErrorsResolver } from '../resolver/anfrage/get-anfrage-validationerrors.resolver';
import { OrganisationFormProcessStepComponent } from '../features/anfrage/forms/organisation-form-process-step/organisation-form-process-step.component';
import { GetAnfrageContactDataResolver } from '../resolver/anfrage/get-anfrage-contact-data.resolver';
import { ReportsGuard } from '../guards/reports/reports.guard';
import { ApplicantFormProcessStepComponent } from '../features/anfrage/forms/applicant-form-process-step/applicant-form-process-step.component';
import { ProjectDataProcessStepComponent } from '../features/anfrage/forms/project-data-process-step/project-data-process-step.component';
import { GetAnfrageProjectDataResolver } from '../resolver/anfrage/get-anfrage-project-data.resolver';
import { FragenProcessStepComponent } from '../features/anfrage/forms/fragen-process-step/fragen-process-step.component';
import { FragenResolver } from '../resolver/fragen/fragen.resolver';
import { FinanceDataProcessStepComponent } from '../features/anfrage/forms/finance-data-process-step/finance-data-process-step.component';
import { GetAnfrageFinanceDataResolver } from '../resolver/anfrage/get-anfrage-finance-data.resolver';
import { GetAllAnfragenDocumenttypesResolver } from '../resolver/anfrage/get-all-anfrage-documenttypes.resolver';
import { AttachmentsProcessStepComponent } from '../features/anfrage/forms/attachments-process-step/attachments-process-step.component';
import { GetAllAnfragenAttachmentsResolver } from '../resolver/anfrage/get-all-anfrage-attachments.resolver';
import { ScheduleDataProcessStepComponent } from '../features/anfrage/forms/schedule-data-process-step/schedule-data-process-step.component';
import { GetAnfrageScheduleDataResolver } from '../resolver/anfrage/get-anfrage-schedule.resolver';
import { AccessableInDraftGuard } from '../guards/anfrage/accessable-in-draft.guard';
import { StipendiumProccessStepComponent } from '../features/anfrage/forms/stipendium-proccess-step/stipendium-proccess-step.component';
import { GetAnfrageStipendiumResolver } from '../resolver/anfrage/get-anfrage-stipendium.resolver';
import { BaseRoutingFactory } from './base-routing.factory';
import { AnfrageOverviewComponent } from '../features/anfrage/components/anfrage-overview/anfrage-overview.component';
import { GetAllProjektProjektideeAnfragenResolver } from '../resolver/anfrage/get-all-projekt-projektidee-anfragen.resolver';
import { DataprotectionProcessStepComponent } from '../forms/dataprotection-process-step/dataprotection-process-step.component';

export class AnfrageRoutingFactory extends BaseRoutingFactory {
    private readonly coreAnfrageRoute: Routes = [
        {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
        },
        {
            path: 'overview',
            component: AnfrageOverviewComponent,
            resolve: {
                anfragen: GetAllProjektProjektideeAnfragenResolver,
            },
        },
        {
            path: 'detail/:scope/:id',
            component: AnfrageDetailComponent,
            canActivate: [AnfrageGuard],
            resolve: {
                anfrage: GetAnfrageByIdResolver
            },
            children: [
                {
                    path: '',
                    redirectTo: 'contact-data',
                    pathMatch: 'full',
                },
                {
                    path: 'contact-data/:abschnittid',
                    component: OrganisationFormProcessStepComponent,
                    canDeactivate: [],
                    resolve: {
                        contactData: GetAnfrageContactDataResolver,
                    },
                },
                {
                    path: 'applicant-form/:abschnittid',
                    component: ApplicantFormProcessStepComponent,
                    canDeactivate: [],
                    resolve: {
                        contactData: GetAnfrageContactDataResolver,
                    },
                },
                {
                    path: 'project-data/:abschnittid',
                    component: ProjectDataProcessStepComponent,
                    canDeactivate: [],
                    resolve: {
                        projectData: GetAnfrageProjectDataResolver,
                    },
                },
                {
                    path: 'questions/:abschnittid',
                    component: FragenProcessStepComponent,
                    resolve: {
                        fragen: FragenResolver,
                    },
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                },
                {
                    path: 'finance-data/:abschnittid',
                    component: FinanceDataProcessStepComponent,
                    canDeactivate: [],
                    resolve: {
                        financeData: GetAnfrageFinanceDataResolver,
                        documenttypes: GetAllAnfragenDocumenttypesResolver,
                    },
                },
                {
                    path: 'attachments/:abschnittid',
                    component: AttachmentsProcessStepComponent,
                    resolve: {
                        contactData: GetAnfrageContactDataResolver,
                        documenttypes: GetAllAnfragenDocumenttypesResolver,
                        attachments: GetAllAnfragenAttachmentsResolver,
                    },
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                },
                {
                    path: 'schedule/:abschnittid',
                    component: ScheduleDataProcessStepComponent,
                    resolve: {
                        scheduleData: GetAnfrageScheduleDataResolver,
                    },
                },
                {
                    path: 'stipendium/:abschnittid',
                    component: StipendiumProccessStepComponent,
                    resolve: {
                        stipendium: GetAnfrageStipendiumResolver,
                    },
                },
                {
                    path: 'dataprotection',
                    component: DataprotectionProcessStepComponent,
                    canActivate: [AccessableInDraftGuard],
                    resolve: {
                        errors: GetAnfrageValidationErrorsResolver,
                    },
                },
            ],
        },
        {
            path: 'funds',
            loadChildren: () => import('@/core/features/funds/funds.module').then((m) => m.FundsModule),
            canActivate: [
                // FundsGuard
            ],
        },
        {
            path: 'reports',
            loadChildren: () => import('@/core/features/reports/reports.module').then((m) => m.ReportsModule),
            canActivate: [ReportsGuard],
        },
    ];

    public create(): Routes {
        const coreRoute = this.coreRoute;

        // How to add a custom form:
        // Extend from this factory and provide it in the app module
        // Override the create method and call the registerCustomForm method
        // For example:
        
        // this.registerChild(coreRoute, {
        //     path: 'customized-project-data/:abschnittid',
        //     component: CustomizedProjectDataProcessStepComponent,
        //     canDeactivate: [],
        //     resolve: {
        //         projectData: GetAnfrageProjectDataResolver,
        //     },
        // });

        return coreRoute;
    }

    protected get coreRoute(): Routes {
        return this.coreAnfrageRoute;
    }

    protected registerChild(coreRoute: Routes, route: Route): void {
        const formRoutes = coreRoute![2].children!;
        const foundIndex = formRoutes?.findIndex(
            (formRoute) => formRoute.path === route.path
        );
        if (foundIndex >= 0) {
            formRoutes[foundIndex] = route;
        } else {
            coreRoute![2].children!.push(route);
        }
    }
}
